import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React from 'react';
import { formatUsdPerMonth } from '../podDashboard.format';
import { PAID_POD_MONTHLY_RATE, SubscribedBreakdown } from '../podDashboard.types';
import StripeLink from './StripeLink';

interface Props {
    subscribed: SubscribedBreakdown;
}

const headerCellSx = {
    color: 'text.secondary',
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    border: 0,
    py: 1,
};

// Fixed column widths so every per-org table lines up identically regardless of
// the longest name in a given group. Used with tableLayout: 'fixed'.
const fixedTableSx = { tableLayout: 'fixed' as const, mb: 2 };
const podsColSx = { ...headerCellSx, width: 140 };
const valueColSx = { ...headerCellSx, width: 160 };

const bodyCellSx = { fontSize: '0.85rem', borderColor: 'grey.100', py: 1.25 };

/** Monthly value counts only Stripe-billed pods; external-invoice pods are excluded. */
const monthlyValue = (pods: number, externalInvoicePods = 0): string =>
    formatUsdPerMonth(Math.max(0, pods - externalInvoicePods) * PAID_POD_MONTHLY_RATE);

const ExternalInvoiceBadge = ({ count }: { count: number }) =>
    count > 0 ? (
        <Chip
            label={`${count} via external invoice`}
            size="small"
            sx={{
                ml: 1,
                height: 18,
                fontSize: '0.65rem',
                backgroundColor: 'grey.200',
                color: 'text.secondary',
            }}
        />
    ) : null;

/**
 * Subscribed drill-down: which organizations are paying, for how many pods, and
 * the estimated monthly value, plus the athlete-subscription subtotal. Pods billed
 * via external invoice are flagged and excluded from the monthly value estimate.
 */
const SubscribedDrilldown = ({ subscribed }: Props) => (
    <Box>
        {subscribed.zeroChargePods > 0 && (
            <Typography
                sx={{
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                    mb: 1.5,
                    fontStyle: 'italic',
                }}
            >
                {subscribed.zeroChargePods.toLocaleString('en-US')}{' '}
                {subscribed.zeroChargePods === 1 ? 'pod bills' : 'pods bill'} $0 via a Stripe coupon
                or credit — counted as subscribed, but excluded from the revenue estimate.
            </Typography>
        )}

        <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Org subscribed
            <Typography component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                {subscribed.org.pods.toLocaleString('en-US')} paid pods ·{' '}
                {monthlyValue(subscribed.org.pods, subscribed.org.externalInvoicePods)}
            </Typography>
            <ExternalInvoiceBadge count={subscribed.org.externalInvoicePods} />
        </Typography>

        {subscribed.org.holders.length > 0 ? (
            <Table size="small" sx={fixedTableSx}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={headerCellSx}>ORGANIZATION</TableCell>
                        <TableCell sx={podsColSx} align="right">
                            SUBSCRIBED PODS
                        </TableCell>
                        <TableCell sx={valueColSx} align="right">
                            MONTHLY VALUE
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {subscribed.org.holders.map((row) => (
                        <TableRow key={row.holder} hover>
                            <TableCell sx={bodyCellSx}>
                                {row.holder}
                                <StripeLink id={row.stripeCustomerId} entity="customers" />
                                <ExternalInvoiceBadge count={row.externalInvoicePods} />
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="right">
                                {row.pods.toLocaleString('en-US')}
                            </TableCell>
                            <TableCell sx={bodyCellSx} align="right">
                                {monthlyValue(row.pods, row.externalInvoicePods)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }}>
                            Total
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }} align="right">
                            {subscribed.org.pods.toLocaleString('en-US')}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, fontWeight: 800, border: 0 }} align="right">
                            {monthlyValue(subscribed.org.pods, subscribed.org.externalInvoicePods)}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        ) : (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 2 }}>
                No paying organizations.
            </Typography>
        )}

        <Typography sx={{ fontWeight: 700, mb: 1 }}>
            Athlete subscribed
            <Typography component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                {subscribed.athlete.pods.toLocaleString('en-US')} pods ·{' '}
                {monthlyValue(subscribed.athlete.pods)}
            </Typography>
        </Typography>

        {subscribed.athlete.orgs.length > 0 ? (
            <>
                {subscribed.athlete.orgs.map((group) => (
                    <Box key={group.holder} sx={{ mb: 2 }}>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: 'text.secondary',
                            }}
                        >
                            {group.holder}
                            <Typography component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                                {group.athletes.length.toLocaleString('en-US')}{' '}
                                {group.athletes.length === 1 ? 'athlete' : 'athletes'} ·{' '}
                                {group.pods.toLocaleString('en-US')} pods ·{' '}
                                {monthlyValue(group.pods)}
                            </Typography>
                        </Typography>
                        <Table size="small" sx={fixedTableSx}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headerCellSx}>ATHLETE</TableCell>
                                    <TableCell sx={podsColSx} align="right">
                                        PODS
                                    </TableCell>
                                    <TableCell sx={valueColSx} align="right">
                                        MONTHLY VALUE
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.athletes.map((athlete, idx) => (
                                    <TableRow
                                        key={athlete.individualUuid ?? `${group.holder}-${idx}`}
                                        hover
                                    >
                                        <TableCell sx={bodyCellSx}>
                                            {athlete.name}
                                            <StripeLink
                                                id={athlete.stripeSubscriptionId}
                                                entity="subscriptions"
                                            />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx} align="right">
                                            {athlete.pods.toLocaleString('en-US')}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx} align="right">
                                            {monthlyValue(athlete.pods)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ))}
            </>
        ) : (
            <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                No subscribed athletes.
            </Typography>
        )}
    </Box>
);

export default SubscribedDrilldown;
